import React from 'react';
import styles from './dialog.module.scss';
import clsx from 'clsx';
import { useFocusable, FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import Typography from '@smart-tv/components/typography';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

function assignRef<T>(ref: React.Ref<T> | undefined, value: T) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref && 'current' in ref) {
    (ref as React.MutableRefObject<T>).current = value;
  }
}

export const DialogContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, forwardedRef) => {
    const { children, className, ...rest } = props;
    const { ref: focusableRef, focusKey } = useFocusable({ isFocusBoundary: true });

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        assignRef(focusableRef, node);
        assignRef(forwardedRef, node);
      },
      [focusableRef, forwardedRef]
    );

    return (
      <FocusContext.Provider value={focusKey}>
        <div className={clsx(styles['dialog-content'], className)} ref={setRefs} {...rest}>
          {children}
        </div>
      </FocusContext.Provider>
    );
  }
);
DialogContent.displayName = 'DialogContent';

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {}

export const Sidebar: React.FC<SidebarProps> = (props: SidebarProps) => {
  const { children, className, ...rest } = props;

  return (
    <DialogContent className={clsx(styles['sidebar'], className)} {...rest}>
      {children}
    </DialogContent>
  );
};
Sidebar.displayName = 'Sidebar';

type TypographyProps = React.ComponentProps<typeof Typography>;

interface SidebarHeaderProps extends Omit<TypographyProps, 'children'> {
  hasDescription?: boolean;
  children: React.ReactNode;
}

export const SidebarHeader: React.FC<SidebarHeaderProps> = (props: SidebarHeaderProps) => {
  const { hasDescription = false, className, children, ...rest } = props;

  return (
    <Typography
      className={clsx(
        styles['header'],
        hasDescription && styles['header--has-description'],
        className
      )}
      {...rest}
    >
      {children}
    </Typography>
  );
};
SidebarHeader.displayName = 'SidebarHeader';

interface SidebarDescriptionProps extends Omit<TypographyProps, 'children'> {
  children: React.ReactNode;
}

export const SidebarDescription: React.FC<SidebarDescriptionProps> = (
  props: SidebarDescriptionProps
) => {
  const { className, children, ...rest } = props;

  return (
    <Typography className={clsx(styles['description'], className)} {...rest}>
      {children}
    </Typography>
  );
};
SidebarDescription.displayName = 'SidebarDescription';

interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> {
  hasFooter?: boolean;
}

export const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(
  (props, forwardedRef) => {
    const { hasFooter = false, className, children, ...rest } = props;

    return (
      <div
        ref={forwardedRef}
        className={clsx(
          styles['content'],
          hasFooter && styles['content--has-footer'],
          IS_BROWSER ? styles['content--scrollable'] : styles['content--non-scrollable'],
          className
        )}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
SidebarContent.displayName = 'SidebarContent';

export default DialogContent;
